import { useState, useEffect, useRef } from 'react';
import PROJECTS from '../data/projects';

// ── Helpers ──────────────────────────────────────────────────────────

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

// Uploads a file to public/images/{projectId}/{filename} via serverless proxy
// and returns the public path /images/{projectId}/{filename}
// Resize large images to stay under GitHub Contents API 1MB limit
const compressImage = (file, maxWidth = 1920, quality = 0.85) =>
  new Promise((resolve) => {
    // Skip non-image or already-small files
    if (!file.type.startsWith('image/') || file.size < 800_000) {
      resolve(file);
      return;
    }
    const img = new Image();
    img.onload = () => {
      const scale = img.width > maxWidth ? maxWidth / img.width : 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) {
            resolve(file); // compression didn't help, keep original
          } else {
            resolve(new File([blob], file.name, { type: blob.type }));
          }
        },
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

const uploadImageToGithub = async (file, projectId) => {
  // Compress before upload to stay within API limits
  const processed = await compressImage(file);
  const sanitizedName = processed.name.replace(/\s+/g, '-').replace(/\.[^.]+$/, '.jpg');
  const path = `public/images/${projectId}/${sanitizedName}`;

  // Read file as base64
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(processed);
  });

  // Check if file already exists (need SHA to overwrite)
  let sha;
  const getRes = await fetch(`/api/github?path=${encodeURIComponent(path)}`);
  if (getRes.ok) {
    const getData = await getRes.json();
    sha = getData.sha;
  }

  const putRes = await fetch('/api/github', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      path,
      message: `Upload image: ${file.name}`,
      content: base64,
      ...(sha ? { sha } : {}),
    }),
  });

  if (!putRes.ok) {
    let errMsg = `Upload failed (${putRes.status})`;
    try {
      const err = await putRes.json();
      errMsg = err.error || err.message || errMsg;
    } catch {
      // Response wasn't JSON (e.g. HTML error page)
    }
    throw new Error(errMsg);
  }

  return `/images/${projectId}/${sanitizedName}`;
};

// ── Toast ────────────────────────────────────────────────────────────

const Toast = ({ toast, onDismiss }) => {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);

  if (!toast) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 32,
        right: 32,
        padding: '14px 24px',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 500,
        zIndex: 100,
        color: '#fff',
        backgroundColor: toast.type === 'error' ? '#dc2626' : '#16a34a',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}
    >
      {toast.message}
    </div>
  );
};

// ── Image List Input (with file browser + GitHub upload) ──────────────

const ImageListInput = ({
  label,
  items,
  onChange,
  placeholder,
  projectId,
  onError,
}) => {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const fileInputRef = useRef(null);
  const pendingIndexRef = useRef(null);
  const drag = useDragReorder(items, (reordered) => onChange(reordered));

  const update = (index, value) => {
    const next = [...items];
    next[index] = value;
    onChange(next);
  };

  const remove = (index) => onChange(items.filter((_, i) => i !== index));

  const add = () => onChange([...items, '']);

  const browse = (index) => {
    pendingIndexRef.current = index;
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    e.target.value = '';

    const startIndex = pendingIndexRef.current;
    const next = [...items];

    for (let i = 0; i < files.length; i++) {
      const idx = startIndex + i;
      setUploadingIndex(idx);
      try {
        const path = await uploadImageToGithub(files[i], projectId || 'images');
        while (next.length <= idx) next.push('');
        next[idx] = path;
        onChange([...next]);
      } catch (err) {
        onError(`Upload failed (${files[i].name}): ${err.message}`);
      }
    }

    setUploadingIndex(null);
    pendingIndexRef.current = null;
  };

  return (
    <div style={{ marginBottom: 28 }}>
      <span
        style={{
          display: 'block',
          color: '#e5e5e5',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0.5,
          marginBottom: 10,
        }}
      >
        {label}
      </span>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {items.map((item, i) => (
        <div
          key={i}
          {...drag.dragProps(i)}
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 8,
            alignItems: 'center',
            opacity: drag.dragIndex === i ? 0.4 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          <DragHandle />
          {/* Path text input */}
          <input
            type="text"
            value={item}
            onChange={(e) => update(i, e.target.value)}
            placeholder={uploadingIndex === i ? 'Uploading...' : placeholder}
            disabled={uploadingIndex === i}
            style={{
              flex: 1,
              padding: '9px 12px',
              backgroundColor: uploadingIndex === i ? '#111' : '#0a0a0a',
              border: '1px solid #262626',
              borderRadius: 6,
              color: uploadingIndex === i ? '#666' : '#fff',
              fontSize: 12,
              fontFamily: 'monospace',
              outline: 'none',
            }}
          />

          {/* Browse button */}
          <button
            onClick={() => browse(i)}
            disabled={uploadingIndex !== null}
            title="Browse for image"
            style={{
              padding: '0 14px',
              height: 38,
              backgroundColor: 'transparent',
              border: '1px solid #333',
              borderRadius: 6,
              color: uploadingIndex === i ? '#555' : '#aaa',
              fontSize: 12,
              fontWeight: 500,
              cursor: uploadingIndex !== null ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (uploadingIndex !== null) return;
              e.currentTarget.style.borderColor = '#666';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#333';
              e.currentTarget.style.color = '#aaa';
            }}
          >
            {uploadingIndex === i ? '...' : 'Browse'}
          </button>

          {/* Remove button */}
          <button
            onClick={() => remove(i)}
            title="Remove"
            style={{
              width: 38,
              height: 38,
              backgroundColor: 'transparent',
              border: '1px solid #262626',
              borderRadius: 6,
              color: '#555',
              fontSize: 18,
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#dc2626';
              e.currentTarget.style.color = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#262626';
              e.currentTarget.style.color = '#555';
            }}
          >
            &times;
          </button>
        </div>
      ))}

      <button
        onClick={add}
        style={{
          padding: '7px 16px',
          backgroundColor: 'transparent',
          border: '1px dashed #333',
          borderRadius: 6,
          color: '#666',
          fontSize: 13,
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#555';
          e.currentTarget.style.color = '#bbb';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#333';
          e.currentTarget.style.color = '#666';
        }}
      >
        + Add image
      </button>
    </div>
  );
};

// ── Paragraph List Input ──────────────────────────────────────────────

const ParagraphListInput = ({ label, items, onChange }) => {
  const drag = useDragReorder(items, (reordered) => onChange(reordered));

  const update = (index, value) => {
    const next = [...items];
    next[index] = value;
    onChange(next);
  };

  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const add = () => onChange([...items, '']);

  return (
    <div style={{ marginBottom: 28 }}>
      <span
        style={{
          display: 'block',
          color: '#e5e5e5',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0.5,
          marginBottom: 10,
        }}
      >
        {label}
      </span>
      {items.map((item, i) => (
        <div
          key={i}
          {...drag.dragProps(i)}
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 10,
            opacity: drag.dragIndex === i ? 0.4 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          <DragHandle style={{ paddingTop: 10 }} />
          <textarea
            value={item}
            onChange={(e) => update(i, e.target.value)}
            placeholder="Write a paragraph..."
            rows={3}
            style={{
              flex: 1,
              padding: '10px 12px',
              backgroundColor: '#0a0a0a',
              border: '1px solid #262626',
              borderRadius: 6,
              color: '#fff',
              fontSize: 13,
              fontFamily: "'Montserrat', sans-serif",
              outline: 'none',
              resize: 'vertical',
              lineHeight: 1.6,
            }}
          />
          <button
            onClick={() => remove(i)}
            title="Remove"
            style={{
              width: 38,
              backgroundColor: 'transparent',
              border: '1px solid #262626',
              borderRadius: 6,
              color: '#555',
              fontSize: 18,
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#dc2626';
              e.currentTarget.style.color = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#262626';
              e.currentTarget.style.color = '#555';
            }}
          >
            &times;
          </button>
        </div>
      ))}
      <button
        onClick={add}
        style={{
          padding: '7px 16px',
          backgroundColor: 'transparent',
          border: '1px dashed #333',
          borderRadius: 6,
          color: '#666',
          fontSize: 13,
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#555';
          e.currentTarget.style.color = '#bbb';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#333';
          e.currentTarget.style.color = '#666';
        }}
      >
        + Add paragraph
      </button>
    </div>
  );
};

// ── Text Field ───────────────────────────────────────────────────────

const TextField = ({ label, value, onChange, placeholder, mono, hint }) => (
  <div style={{ marginBottom: 22 }}>
    <span
      style={{
        display: 'block',
        color: '#e5e5e5',
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: 0.5,
        marginBottom: hint ? 2 : 6,
      }}
    >
      {label}
    </span>
    {hint && (
      <span style={{ display: 'block', color: '#666', fontSize: 11, marginBottom: 6 }}>
        {hint}
      </span>
    )}
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        display: 'block',
        width: '100%',
        padding: '10px 12px',
        backgroundColor: '#0a0a0a',
        border: '1px solid #262626',
        borderRadius: 6,
        color: '#fff',
        fontSize: 14,
        fontFamily: mono ? 'monospace' : "'Montserrat', sans-serif",
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s',
      }}
      onFocus={(e) => (e.target.style.borderColor = '#555')}
      onBlur={(e) => (e.target.style.borderColor = '#262626')}
    />
  </div>
);

// ── Section Header ───────────────────────────────────────────────────

const SectionHeader = ({ children }) => (
  <h3
    style={{
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 3,
      color: '#fff',
      marginBottom: 20,
      paddingBottom: 10,
      borderBottom: '1px solid #222',
    }}
  >
    {children}
  </h3>
);

// ── Drag Reorder Hook ────────────────────────────────────────────────

const useDragReorder = (items, onReorder) => {
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);

  const onDragStart = (index) => (e) => {
    dragItem.current = index;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragEnter = (index) => (e) => {
    e.preventDefault();
    dragOverItem.current = index;
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (e) => {
    e.preventDefault();
    const from = dragItem.current;
    const to = dragOverItem.current;
    if (from === null || to === null || from === to) {
      setDragIndex(null);
      return;
    }
    const reordered = [...items];
    const [removed] = reordered.splice(from, 1);
    reordered.splice(to, 0, removed);
    onReorder(reordered, from, to);
    dragItem.current = null;
    dragOverItem.current = null;
    setDragIndex(null);
  };

  const onDragEnd = () => {
    dragItem.current = null;
    dragOverItem.current = null;
    setDragIndex(null);
  };

  const dragProps = (index) => ({
    draggable: true,
    onDragStart: onDragStart(index),
    onDragEnter: onDragEnter(index),
    onDragOver,
    onDrop,
    onDragEnd,
  });

  return { dragIndex, dragProps };
};

const DragHandle = ({ style }) => (
  <span style={{
    color: '#333',
    cursor: 'grab',
    fontSize: 14,
    padding: '0 2px',
    userSelect: 'none',
    flexShrink: 0,
    ...style,
  }}>
    &#8801;
  </span>
);

// ── Main Admin Component ─────────────────────────────────────────────

const Admin = () => {
  const [projects, setProjects] = useState(PROJECTS);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(0);

  const reorderProjects = (reordered) => {
    const currentProject = projects[selectedIndex];
    setProjects(reordered);
    const newIndex = reordered.indexOf(currentProject);
    setSelectedIndex(newIndex >= 0 ? newIndex : 0);
  };

  const projectDrag = useDragReorder(projects, reorderProjects);

  const showToast = (type, message) => setToast({ type, message });

  const project = projects[selectedIndex];

  const updateField = (field, value) => {
    setProjects((prev) =>
      prev.map((p, i) => (i === selectedIndex ? { ...p, [field]: value } : p))
    );
  };

  const updateTitle = (value) => {
    const oldSlug = slugify(project.title);
    const shouldAutoSlug = !project.id || project.id === oldSlug;
    setProjects((prev) =>
      prev.map((p, i) =>
        i === selectedIndex
          ? { ...p, title: value, ...(shouldAutoSlug ? { id: slugify(value) } : {}) }
          : p
      )
    );
  };

  const addProject = () => {
    const newProject = {
      id: '',
      title: '',
      videoPlaybackId: '',
      videoTitle: '',
      detailHeading: '',
      paragraphs: [''],
      images: [],
      mobileImages: [],
    };
    setProjects((prev) => [...prev, newProject]);
    setSelectedIndex(projects.length);
  };

  const deleteProject = () => {
    if (projects.length <= 1) return;
    setProjects((prev) => prev.filter((_, i) => i !== selectedIndex));
    setSelectedIndex(Math.max(0, selectedIndex - 1));
    setConfirmDelete(false);
  };

  const generateFileContent = () => {
    const json = JSON.stringify(projects, null, 2);
    return [
      '// ── Central project configuration ────────────────────────────────────',
      '// To add a new project, add an object below with all fields filled in.',
      '// The MyWork slide, detail page, and route are all generated automatically.',
      '// Or use the admin panel at /admin to edit visually.',
      '',
      `const PROJECTS = ${json};`,
      '',
      'export default PROJECTS;',
      '',
    ].join('\n');
  };

  const SAVE_COOLDOWN = 10000;

  const saveToGithub = async () => {
    const now = Date.now();
    const elapsed = now - lastSaveTime;
    if (elapsed < SAVE_COOLDOWN) {
      const wait = Math.ceil((SAVE_COOLDOWN - elapsed) / 1000);
      showToast('error', `Please wait ${wait}s before saving again`);
      return;
    }

    for (const p of projects) {
      if (!p.id || !p.title) {
        showToast('error', `Project "${p.title || '(untitled)'}" needs an ID and title`);
        return;
      }
    }

    setSaving(true);
    try {
      const path = 'src/data/projects.js';

      let sha;
      const getRes = await fetch(`/api/github?path=${encodeURIComponent(path)}`);
      if (getRes.ok) {
        const getData = await getRes.json();
        sha = getData.sha;
      } else if (getRes.status !== 404) {
        let errMsg = `Failed to reach repository (${getRes.status})`;
        try {
          const err = await getRes.json();
          errMsg = err.error || err.message || errMsg;
        } catch {}
        throw new Error(errMsg);
      }

      const content = generateFileContent();
      const putRes = await fetch('/api/github', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path,
          message: sha ? 'Update projects via admin panel' : 'Add projects config via admin panel',
          content: btoa(unescape(encodeURIComponent(content))),
          ...(sha ? { sha } : {}),
        }),
      });

      if (!putRes.ok) {
        let errMsg = `Failed to save (${putRes.status})`;
        try {
          const err = await putRes.json();
          errMsg = err.error || err.message || errMsg;
        } catch {}
        throw new Error(errMsg);
      }

      setLastSaveTime(Date.now());
      showToast('success', 'Saved! Vercel will redeploy shortly.');
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!project) return null;

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        backgroundColor: '#0a0a0a',
        fontFamily: "'Montserrat', sans-serif",
        color: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* ── Sidebar ── */}
      <aside
        style={{
          width: 240,
          minWidth: 240,
          borderRight: '1px solid #1a1a1a',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#0d0d0d',
        }}
      >
        <div
          style={{
            padding: '22px 18px 14px',
            borderBottom: '1px solid #1a1a1a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: '#fff' }}>
            Projects
          </span>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '10px' }}>
          {projects.map((p, i) => (
            <div
              key={i}
              {...projectDrag.dragProps(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                marginBottom: 3,
                opacity: projectDrag.dragIndex === i ? 0.4 : 1,
                transition: 'opacity 0.15s',
              }}
            >
              <DragHandle />
              <button
                onClick={() => { setSelectedIndex(i); setConfirmDelete(false); }}
                style={{
                  display: 'block',
                  flex: 1,
                  textAlign: 'left',
                  padding: '11px 13px',
                  backgroundColor: i === selectedIndex ? '#1c1c1c' : 'transparent',
                  border: i === selectedIndex ? '1px solid #333' : '1px solid transparent',
                  borderRadius: 7,
                  color: i === selectedIndex ? '#fff' : '#777',
                  fontSize: 12,
                  fontWeight: i === selectedIndex ? 700 : 400,
                  fontFamily: "'Montserrat', sans-serif",
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  letterSpacing: 0.5,
                }}
                onMouseEnter={(e) => { if (i !== selectedIndex) e.currentTarget.style.color = '#ccc'; }}
                onMouseLeave={(e) => { if (i !== selectedIndex) e.currentTarget.style.color = '#777'; }}
              >
                {p.title || '(untitled)'}
              </button>
            </div>
          ))}
        </div>

        <div style={{ padding: '10px', borderTop: '1px solid #1a1a1a' }}>
          <button
            onClick={addProject}
            style={{
              width: '100%',
              padding: '10px 0',
              backgroundColor: 'transparent',
              border: '1px dashed #2a2a2a',
              borderRadius: 7,
              color: '#555',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: 0.5,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.color = '#bbb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#555'; }}
          >
            + New Project
          </button>
        </div>
      </aside>

      {/* ── Main Editor ── */}
      <main style={{ flex: 1, overflow: 'auto' }}>
        {/* Top bar */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            padding: '14px 32px',
            borderBottom: '1px solid #1a1a1a',
            backgroundColor: '#0a0a0a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ color: '#444', fontSize: 12, fontFamily: 'monospace' }}>
            /project/{project.id || '...'}
          </span>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {projects.length > 1 && (
              confirmDelete ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ color: '#888', fontSize: 12 }}>Delete this project?</span>
                  <button
                    onClick={deleteProject}
                    style={{ padding: '6px 14px', backgroundColor: '#dc2626', border: 'none', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    style={{ padding: '6px 14px', backgroundColor: '#222', border: 'none', borderRadius: 6, color: '#999', fontSize: 12, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  style={{ padding: '6px 14px', backgroundColor: 'transparent', border: '1px solid #222', borderRadius: 6, color: '#555', fontSize: 12, cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#dc2626'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#222'; e.currentTarget.style.color = '#555'; }}
                >
                  Delete
                </button>
              )
            )}
            <button
              onClick={saveToGithub}
              disabled={saving}
              style={{
                padding: '8px 22px',
                backgroundColor: saving ? '#1a1a1a' : '#fff',
                color: saving ? '#555' : '#000',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: "'Montserrat', sans-serif",
                letterSpacing: 0.5,
                transition: 'all 0.2s',
              }}
            >
              {saving ? 'Saving...' : 'Save & Deploy'}
            </button>
          </div>
        </div>

        {/* Form */}
        <div style={{ padding: '36px 40px 100px', maxWidth: 680 }}>

          {/* Identity */}
          <div style={{ marginBottom: 44 }}>
            <SectionHeader>Identity</SectionHeader>
            <TextField
              label="Title"
              value={project.title}
              onChange={updateTitle}
              placeholder="PROJECT NAME"
              hint="Shown on the /work page slide"
            />
            <TextField
              label="ID — URL Slug"
              value={project.id}
              onChange={(v) => updateField('id', v)}
              placeholder="project-name"
              hint="Auto-generated from title. Page will be at /project/your-id"
              mono
            />
          </div>

          {/* Video */}
          <div style={{ marginBottom: 44 }}>
            <SectionHeader>Video</SectionHeader>
            <TextField
              label="Mux Playback ID"
              value={project.videoPlaybackId}
              onChange={(v) => updateField('videoPlaybackId', v)}
              placeholder="YFmaFTJUnPhQ..."
              hint="Found in your Mux dashboard under the asset details"
              mono
            />
            <TextField
              label="Video Title"
              value={project.videoTitle}
              onChange={(v) => updateField('videoTitle', v)}
              placeholder="Project Name"
              hint="Shown in the video player (accessibility / hover)"
            />
          </div>

          {/* Detail Page */}
          <div style={{ marginBottom: 44 }}>
            <SectionHeader>Detail Page</SectionHeader>
            <TextField
              label="Heading"
              value={project.detailHeading}
              onChange={(v) => updateField('detailHeading', v)}
              placeholder="Project Full Name"
              hint="The large heading shown below the video"
            />
            <ParagraphListInput
              label="Paragraphs"
              items={project.paragraphs}
              onChange={(v) => updateField('paragraphs', v)}
            />
          </div>

          {/* Images */}
          <div style={{ marginBottom: 44 }}>
            <SectionHeader>Slideshow Images</SectionHeader>
            <p style={{ color: '#555', fontSize: 12, marginBottom: 20, lineHeight: 1.6 }}>
              Click <strong style={{ color: '#888' }}>Browse</strong> to pick an image from your computer — it uploads
              directly to your repo in <code style={{ color: '#777', fontFamily: 'monospace' }}>public/images/{project.id || 'project-id'}/</code>
              {' '}and sets the path automatically. Or type a path manually.
            </p>
            <ImageListInput
              label="Desktop Images (16:9)"
              items={project.images}
              onChange={(v) => updateField('images', v)}
              placeholder="/images/folder/filename.webp"
              projectId={project.id}
              onError={(msg) => showToast('error', msg)}
            />
            <ImageListInput
              label="Mobile Images (9:16)"
              items={project.mobileImages}
              onChange={(v) => updateField('mobileImages', v)}
              placeholder="/images/folder-mobile/filename.webp"
              projectId={project.id}
              onError={(msg) => showToast('error', msg)}
            />
          </div>

        </div>
      </main>

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
};

// ── Password Gate ────────────────────────────────────────────────────

const SESSION_KEY = 'admin_session';

const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setChecking(true);
    setError('');
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        sessionStorage.setItem(SESSION_KEY, '1');
        onLogin();
      } else {
        setError('Incorrect password');
        setPassword('');
      }
    } catch {
      setError('Unable to verify — check your connection');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: '#0a0a0a',
      fontFamily: "'Montserrat', sans-serif",
    }}>
      <form onSubmit={handleSubmit} style={{
        width: 340,
        padding: 32,
        backgroundColor: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
      }}>
        <h2 style={{
          color: '#fff',
          fontSize: 16,
          fontWeight: 600,
          marginBottom: 24,
          textAlign: 'center',
          letterSpacing: 1,
        }}>
          Admin Access
        </h2>
        {error && (
          <div style={{
            color: '#dc2626',
            fontSize: 13,
            marginBottom: 14,
            textAlign: 'center',
          }}>
            {error}
          </div>
        )}
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter password"
          autoFocus
          style={{
            display: 'block',
            width: '100%',
            padding: '10px 12px',
            backgroundColor: '#0a0a0a',
            border: '1px solid #333',
            borderRadius: 6,
            color: '#fff',
            fontSize: 14,
            fontFamily: 'monospace',
            outline: 'none',
            boxSizing: 'border-box',
            marginBottom: 16,
          }}
        />
        <button
          type="submit"
          disabled={checking}
          style={{
            width: '100%',
            padding: '10px 0',
            backgroundColor: checking ? '#1a1a1a' : '#fff',
            color: checking ? '#555' : '#000',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 600,
            cursor: checking ? 'not-allowed' : 'pointer',
          }}
        >
          {checking ? 'Checking...' : 'Enter'}
        </button>
      </form>
    </div>
  );
};

const AdminWithAuth = () => {
  const [authenticated, setAuthenticated] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === '1'
  );
  const [loading, setLoading] = useState(!authenticated);

  // On mount, check if password protection is enabled
  useEffect(() => {
    if (authenticated) return;
    // Try empty password — if no ADMIN_PASSWORD is set, the API returns 200
    fetch('/api/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: '' }),
    })
      .then((res) => {
        if (res.ok) {
          // No password configured — skip the gate
          sessionStorage.setItem(SESSION_KEY, '1');
          setAuthenticated(true);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authenticated]);

  if (loading) return null;

  if (!authenticated) {
    return <AdminLogin onLogin={() => setAuthenticated(true)} />;
  }

  return <Admin />;
};

export default AdminWithAuth;
